import React, { useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  ReceiptLong as InvoicesIcon,
  WorkspacePremium as SubscriptionsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';

export type SidebarAccountVariant = 'dark' | 'light';

export interface SidebarAccountLinks {
  profile: string;
  subscriptions: string;
  invoices: string;
}

interface SidebarAccountFooterProps {
  links: SidebarAccountLinks;
  onLogout: () => void;
  collapsed?: boolean;
  variant?: SidebarAccountVariant;
}

function isPlaceholderEmail(email?: string | null) {
  const value = String(email || '').trim();
  if (!value) return true;
  return value.toLowerCase().endsWith('@users.encycgames.invalid');
}

export function accountDisplayName(user: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
} | null | undefined): string {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (!isPlaceholderEmail(user?.email)) return String(user?.email).trim();
  return 'Account';
}

const SidebarAccountFooter: React.FC<SidebarAccountFooterProps> = ({
  links,
  onLogout,
  collapsed = false,
  variant = 'dark',
}) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const name = accountDisplayName(user);
  const dark = variant === 'dark';

  const closeMenu = () => setAnchorEl(null);

  const go = (path: string) => {
    closeMenu();
    navigate(path);
  };

  return (
    <Box
      sx={{
        mt: 'auto',
        borderTop: dark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(17, 23, 57, 0.08)',
        px: collapsed ? 0.75 : 1.25,
        py: 0.85,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 48,
      }}
    >
      {!collapsed && (
        <Typography
          noWrap
          title={name}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.82rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: dark ? '#ecf0f1' : '#111739',
            fontFamily: '"Sora", Arial, sans-serif',
          }}
        >
          {name}
        </Typography>
      )}
      <IconButton
        size="small"
        aria-label="Account menu"
        aria-controls={open ? 'sidebar-account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          ml: collapsed ? 0 : 'auto',
          color: dark ? 'rgba(255,255,255,0.78)' : '#465069',
          '&:hover': {
            bgcolor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(17, 23, 57, 0.06)',
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        id="sidebar-account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 188,
              mt: -0.5,
              boxShadow: '0 8px 24px rgba(17, 23, 57, 0.12)',
              border: '1px solid #e2e8f0',
            },
          },
        }}
      >
        <MenuItem onClick={() => go(links.profile)} sx={{ py: 0.85 }}>
          <ListItemIcon sx={{ minWidth: 34 }}>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 650 }} />
        </MenuItem>
        <MenuItem onClick={() => go(links.subscriptions)} sx={{ py: 0.85 }}>
          <ListItemIcon sx={{ minWidth: 34 }}>
            <SubscriptionsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Subscriptions" primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 650 }} />
        </MenuItem>
        <MenuItem onClick={() => go(links.invoices)} sx={{ py: 0.85 }}>
          <ListItemIcon sx={{ minWidth: 34 }}>
            <InvoicesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Billing invoices" primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 650 }} />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            closeMenu();
            onLogout();
          }}
          sx={{ py: 0.85 }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 650 }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SidebarAccountFooter;
